"use client";

import { c as _c } from "react/compiler-runtime";
import type S3 from "aws-sdk/clients/s3";
import axios from "axios";
import type { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import { api } from "#/shared/api";
import { getImageUrl } from "#/shared/lib";
type SessionHook = ReturnType<typeof useSession>;
type Status = "loading" | "done";
const getAvatarFileName = (username: string, url: string, imageBlob: Blob) => {
  const origin = new URL(url).hostname.replaceAll(".", "-");
  return `avatars/${username.replaceAll(" ", "_")}-avatar[${origin}].${imageBlob.type.split("/")[1]}`;
};
export const useProfileImageUploader = session => {
  const $ = _c(8);
  const sessionBucketImage = session.data?.user.bucketImage;
  const [missingBucketImage, setMissingBucketImage] = useState();
  const bucketImage = sessionBucketImage === missingBucketImage ? undefined : sessionBucketImage;
  const [status, setStatus] = useState(bucketImage ? "done" : "loading");
  const [isLoading, setIsLoading] = useState(false);
  const mutation = api.user.setBucketImage.useMutation();
  const mutationRef = useRef(mutation);
  let t0;
  if ($[0] !== mutation) {
    t0 = () => {
      mutationRef.current = mutation;
    };
    $[0] = mutation;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(t0);
  let t1;
  let t2;
  if ($[2] !== bucketImage || $[3] !== isLoading || $[4] !== session.data || $[5] !== status) {
    t1 = () => {
      if (status === "done" || isLoading) {
        return;
      }
      (async () => {
        if (bucketImage) {
          ;
          try {
            await axios.head(getImageUrl(bucketImage) + "?no-cache");
          } catch (t3) {
            const error = t3;
            if (axios.isAxiosError(error) && error.response?.status === 404) {
              await mutationRef.current.mutateAsync({
                imageName: undefined
              });
              setMissingBucketImage(bucketImage);
            }
          }
        }
        if (!session.data?.user.image || bucketImage) {
          return;
        }
        setIsLoading(true);
        const {
          data: imageBlob
        } = await axios.get(session.data.user.image, {
          responseType: "blob"
        });
        const filename = getAvatarFileName(session.data.user.name || session.data.user.id, session.data.user.image, imageBlob);
        const fileType = encodeURIComponent(imageBlob.type);
        const file = new File([imageBlob], filename);
        const awsResponse = await axios.get(`/api/upload-url?file=${encodeURIComponent(filename)}&fileType=${fileType}`);
        const {
          url,
          fields
        } = awsResponse.data;
        const formData = new FormData();
        Object.entries({
          ...fields,
          file
        }).forEach(t4 => {
          const [key, value] = t4;
          formData.append(key, value as string);
        });
        await axios.post(url, formData);
        await mutationRef.current.mutateAsync({
          imageName: filename
        });
        setStatus("done");
        setIsLoading(false);
      })();
    };
    t2 = [bucketImage, isLoading, session.data, status];
    $[2] = bucketImage;
    $[3] = isLoading;
    $[4] = session.data;
    $[5] = status;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  return status;
};